import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { categoryService, reelService } from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';
import '../../styles/AdminCategoryManager.css';

const AdminCategoryManager = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { admin } = useAuth();
  const [category, setCategory] = useState(null);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReel, setNewReel] = useState({
    title: '',
    description: '',
    sequenceNumber: 0,
    video: null,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingReelId, setEditingReelId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmReelId, setConfirmReelId] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        const res = await categoryService.getCategoryById(categoryId);
        setCategory(res.data);
        setReels(res.data.reels || []);
      } catch (err) {
        console.error('Error fetching category:', err);
        navigate('/admin/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryData();
  }, [categoryId, navigate]);

  // Re-define fetchCategoryData outside useEffect so other functions can call it
  const refreshCategoryData = async () => {
    try {
      const res = await categoryService.getCategoryById(categoryId);
      setCategory(res.data);
      setReels(res.data.reels || []);
    } catch (err) {
      console.error('Error fetching category:', err);
    }
  };

  const handleEditReel = (reel) => {
    setNewReel({
      title: reel.title,
      description: reel.description || '',
      sequenceNumber: reel.sequence_number || 0,
      video: null, // Don't require video re-upload
    });
    setEditingReelId(reel.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setNewReel({ title: '', description: '', sequenceNumber: 0, video: null });
    setEditingReelId(null);
    setShowForm(false);
  };

  const handleSubmitReel = async (e) => {
    e.preventDefault();
    if (!newReel.title) {
      alert('Please enter title');
      return;
    }
    if (!editingReelId && !newReel.video) {
      alert('Please select a video');
      return;
    }

    const formData = new FormData();
    formData.append('title', newReel.title);
    formData.append('description', newReel.description);
    formData.append('categoryId', categoryId);
    formData.append('sequenceNumber', newReel.sequenceNumber);
    if (newReel.video) {
      formData.append('video', newReel.video);
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      if (editingReelId) {
        await reelService.updateReel(editingReelId, formData);
      } else {
        await reelService.createReel(formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
      }

      setNewReel({ title: '', description: '', sequenceNumber: 0, video: null });
      setEditingReelId(null);
      setShowForm(false);
      setUploadProgress(0);
      await refreshCategoryData();
    } catch (err) {
      alert(`Error ${editingReelId ? 'updating' : 'creating'} reel: ` + (err.response?.data?.error || err.message));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteReel = (reelId) => {
    setConfirmReelId(reelId);
    setConfirmMessage('Are you sure you want to delete this reel? This action cannot be undone.');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (!confirmReelId) return;

    try {
      await reelService.deleteReel(confirmReelId);
      setConfirmReelId(null);
      await refreshCategoryData();
    } catch (err) {
      alert('Error deleting reel: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setConfirmReelId(null);
  };

  const downloadQRCode = (format) => {
    if (!category?.qr_code?.qr_code_url) return;

    const qrDataUrl = category.qr_code.qr_code_url;

    if (format === 'svg') {
      // For SVG, we'll convert the data URL to SVG
      const link = document.createElement('a');
      link.download = `${category.name}-qr-code.svg`;
      link.href = qrDataUrl;
      link.click();
    } else if (format === 'png') {
      // Create a canvas to resize the QR code to 512x512
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, 512, 512);
        ctx.drawImage(img, 0, 0, 512, 512);

        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${category.name}-qr-code-512x512.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        }, 'image/png');
      };
      img.src = qrDataUrl;
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (!category) {
    return <div className="admin-loading">Category not found</div>;
  }

  return (
    <div className="admin-category-manager">
      <ConfirmationModal
        isOpen={confirmOpen}
        title="Confirm Delete"
        message={confirmMessage}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <div className="manager-header">
        <h1>{category.name}</h1>
      </div>

      <div className="manager-content">
        <div className="reels-section">
          <div className="section-header">
            <h2>Reels ({reels.length})</h2>
            <button
              onClick={() => {
                if (showForm) {
                  handleCancelEdit();
                } else {
                  setShowForm(true);
                }
              }}
              className="add-btn"
            >
              {showForm ? '✕ Cancel' : '+ Add Reel'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmitReel} className="reel-form">
              <h3>{editingReelId ? 'Edit Reel' : 'Add New Reel'}</h3>
              <div className="form-group">
                <label htmlFor="reelTitle">Reel Title</label>
                <input
                  id="reelTitle"
                  type="text"
                  value={newReel.title}
                  onChange={(e) =>
                    setNewReel({ ...newReel, title: e.target.value })
                  }
                  placeholder="Enter reel title"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="reelDescription">Description</label>
                <textarea
                  id="reelDescription"
                  value={newReel.description}
                  onChange={(e) =>
                    setNewReel({ ...newReel, description: e.target.value })
                  }
                  placeholder="Enter reel description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label htmlFor="reelSequence">Order (Sequence Number)</label>
                <input
                  id="reelSequence"
                  type="number"
                  value={newReel.sequenceNumber}
                  onChange={(e) =>
                    setNewReel({ ...newReel, sequenceNumber: parseInt(e.target.value) || 0 })
                  }
                  placeholder="0"
                />
                <small>Lower numbers appear first (e.g., 1, 2, 3)</small>
              </div>
              <div className="form-group">
                <label htmlFor="reelVideo">Video File {editingReelId && '(Leave empty to keep current video)'}</label>
                <input
                  id="reelVideo"
                  type="file"
                  accept="video/*"
                  onChange={(e) =>
                    setNewReel({ ...newReel, video: e.target.files[0] })
                  }
                  required={!editingReelId}
                />
                <small>Max 500MB. Supported: MP4, WebM</small>
              </div>
              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{uploadProgress}%</span>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isUploading}>
                  {isUploading ? `Uploading... ${uploadProgress}%` : (editingReelId ? 'Update Reel' : 'Upload Reel')}
                </button>
              </div>
            </form>
          )}

          <div className="reels-list">
            {reels.length === 0 ? (
              <p className="no-data">No reels in this category</p>
            ) : (
              reels.map((reel) => (
                <div key={reel.id} className="reel-item">
                  <div className="reel-thumbnail">
                    <div className="thumbnail-icon">🎬</div>
                  </div>
                  <div className="admin-reel-info">
                    <div className="admin-reel-title-section">
                      <h3>{reel.title} <span className="sequence-badge">#{reel.sequence_number || 0}</span></h3>
                      {reel.description && (
                        <p className="admin-reel-description">{reel.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="reel-actions">
                    <button
                      onClick={() => handleEditReel(reel)}
                      className="edit-btn"
                      style={{ marginRight: '10px' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteReel(reel.id)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {category.qr_code && (
          <div className="qr-section">
            <h2>QR Code</h2>
            <img
              src={category.qr_code.qr_code_url}
              alt="QR Code"
              className="qr-code-image"
            />
            <p className="qr-note">Scan this QR code to view reels in this category</p>
            <div className="qr-download-buttons">
              <button onClick={() => downloadQRCode('svg')} className="qr-download-btn">
                📥 Download SVG
              </button>
              <button onClick={() => downloadQRCode('png')} className="qr-download-btn">
                📥 Download PNG (512x512)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategoryManager;
